import {
    AddAPhoto,
    ArrowBack,
    CheckCircleOutline,
    Close,
    ControlPoint,
    DeleteForever,
    RemoveCircleOutline,
} from '@mui/icons-material'
import { FlexDiv } from '../../components'
import { useEffect, useState } from 'react'
import { Button, TextField, useTheme, Typography, Box, Chip, Alert } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { axiosInstance } from '../../helpers'
import { useAppDispatch } from '../../hooks'
import { setInventoryInspection } from '../../store/Inspection/action'
import { IssueConfig } from '../../types/aptConfig'
import { IDBoolMap } from '../../types'
import { IssueImage } from './IssueImage'
import { InventoryInspection, Issue } from '../../types/inspection'
import { useNavigate } from 'react-router-dom'
import { ResponsivePageContainer } from '../../components/PageContainers'

export const Issues = () => {
    const [customIssue, setCustomIssue] = useState('')

    const navigate = useNavigate()

    const theme = useTheme()
    const dispatch = useAppDispatch()

    const inventoryInspection = useSelector(
        (state: RootState) => state.inspection.inventoryInspection,
    )
    const inventoryConfig = useSelector(
        (state: RootState) => state.inspection.inventoryConfig,
    )
    const areaInspection = useSelector(
        (state: RootState) => state.inspection.areaInspection,
    )

    const leaseId = useSelector((state: RootState) => state.user.leaseId)

    const addIssue = (
        inventoryInspection: InventoryInspection,
        issueConfig?: IssueConfig,
        customIssue?: string,
    ) => {
        axiosInstance
            .post('inspection/tenant-inspection/add_issue_to_inspection/', {
                inventory_inspection_id: inventoryInspection.id,
                issue_config_id: issueConfig?.id,
                description: customIssue,
            })
            .then((res) => {
                setCustomIssue('')
                dispatch(
                    setInventoryInspection({
                        ...inventoryInspection,
                        issue: [...inventoryInspection.issue, res.data],
                    }),
                )
            })
    }

    return (
        <ResponsivePageContainer>
            <FlexDiv
                direction="column"
                style={{
                    maxWidth: '800px',
                    width: '100%',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        backgroundColor: '#2a2f35',
                        borderRadius: 3,
                        padding: 3,
                        marginBottom: 3,
                        border: `1px solid ${theme.palette.grey[700]}`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <FlexDiv
                        style={{
                            alignItems: 'center',
                            marginBottom: theme.spacing(2),
                        }}
                        onClick={() => {
                            navigate(`../select-area/${leaseId}/`)
                        }}
                    >
                        <ArrowBack
                            style={{
                                color: theme.palette.primary.main,
                                marginRight: theme.spacing(1),
                                cursor: 'pointer'
                            }}
                        />
                        <Typography
                            variant="body1"
                            style={{
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Back to Areas
                        </Typography>
                    </FlexDiv>

                    <Typography
                        variant="h4"
                        style={{
                            color: '#fff',
                            marginBottom: theme.spacing(1),
                            fontWeight: 600,
                        }}
                    >
                        {inventoryConfig?.name}
                    </Typography>

                    <Alert
                        severity="info"
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: `1px solid ${theme.palette.info.main}40`,
                            color: '#fff',
                            '& .MuiAlert-icon': {
                                color: theme.palette.info.main,
                            }
                        }}
                    >
                        <Typography variant="body2" style={{ color: '#fff' }}>
                            Adding images is highly recommended (pictures are added after submitting issues)
                        </Typography>
                    </Alert>
                </Box>

                {/* Issues List */}
                <Box
                    sx={{
                        backgroundColor: '#2a2f35',
                        borderRadius: 3,
                        padding: 3,
                        marginBottom: 3,
                        border: `1px solid ${theme.palette.grey[700]}`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                    }}
                >
                    <Typography
                        variant="h6"
                        style={{
                            color: '#fff',
                            marginBottom: theme.spacing(2),
                            fontWeight: 600,
                        }}
                    >
                        Current Issues ({inventoryInspection?.issue.length || 0})
                    </Typography>

                    {inventoryInspection?.issue.map((issue, idx) => {
                        const displayName = issue.issue_config
                            ? issue.issue_config.name
                            : issue.description
                        return (
                            <Box
                                key={`ISSUE_${issue.id}_INVENTORY_${inventoryConfig?.id}_INS_${inventoryInspection.id}_${idx}`}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: 2,
                                    padding: 2,
                                    marginBottom: 2,
                                    border: `1px solid ${theme.palette.grey[600]}`,
                                }}
                            >
                                <FlexDiv style={{ alignItems: 'center', marginBottom: theme.spacing(1) }}>
                                    <Typography
                                        variant="h6"
                                        style={{
                                            color: '#fff',
                                            fontWeight: 600,
                                            flex: 1,
                                        }}
                                    >
                                        {displayName}
                                    </Typography>
                                    <Button
                                        variant="text"
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    `Are you sure you want to permanently remove the ${displayName} issue?`,
                                                )
                                            ) {
                                                axiosInstance
                                                    .post(
                                                        'inspection/tenant-inspection/remove_issue/',
                                                        { issue: issue.id },
                                                    )
                                                    .then(() => {
                                                        const newIssueList =
                                                            inventoryInspection.issue.reduce<
                                                                Issue[]
                                                            >(
                                                                (
                                                                    prev,
                                                                    innerIssue,
                                                                ) => {
                                                                    if (
                                                                        innerIssue.id ===
                                                                        issue.id
                                                                    ) {
                                                                        return prev
                                                                    } else {
                                                                        return prev.concat(
                                                                            innerIssue,
                                                                        )
                                                                    }
                                                                },
                                                                [],
                                                            )
                                                        dispatch(
                                                            setInventoryInspection({
                                                                ...inventoryInspection,
                                                                issue: newIssueList,
                                                            }),
                                                        )
                                                    })
                                                    .catch((err) =>
                                                        console.log(err),
                                                    )
                                            }
                                        }}
                                        sx={{
                                            color: theme.palette.error.main,
                                            minWidth: 'auto',
                                            padding: 1,
                                            '&:hover': {
                                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                            }
                                        }}
                                    >
                                        <DeleteForever />
                                    </Button>
                                </FlexDiv>

                                <FlexDiv style={{ gap: 1 }}>
                                    <IssueImage
                                        index={0}
                                        issue={issue}
                                        inventoryInspection={inventoryInspection}
                                    />
                                    <IssueImage
                                        index={1}
                                        issue={issue}
                                        inventoryInspection={inventoryInspection}
                                    />
                                    <IssueImage
                                        index={2}
                                        issue={issue}
                                        inventoryInspection={inventoryInspection}
                                    />
                                </FlexDiv>
                            </Box>
                        )
                    })}

                    {(!inventoryInspection?.issue || inventoryInspection.issue.length === 0) && (
                        <Box
                            sx={{
                                textAlign: 'center',
                                padding: 4,
                                color: '#8b949e',
                            }}
                        >
                            <Typography variant="body1">
                                No issues added yet. Add your first issue below.
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Add New Issue */}
                {(areaInspection?.status === 'READY' ||
                    areaInspection?.status === 'STAGED' ||
                    areaInspection?.status === 'LOCKED') && (
                    <Box
                        sx={{
                            backgroundColor: '#2a2f35',
                            borderRadius: 3,
                            padding: 3,
                            marginBottom: 3,
                            border: `1px solid ${theme.palette.grey[700]}`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{
                                color: '#fff',
                                marginBottom: theme.spacing(2),
                                fontWeight: 600,
                            }}
                        >
                            Add New Issue
                        </Typography>

                        <FlexDiv style={{ alignItems: 'center', gap: 2 }}>
                            <TextField
                                variant="outlined"
                                value={customIssue}
                                onChange={(e) => setCustomIssue(e.target.value)}
                                placeholder="Describe the issue..."
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        '& fieldset': {
                                            borderColor: theme.palette.grey[600],
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#8b949e',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#8b949e',
                                        opacity: 1,
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                onClick={() => {
                                    if (inventoryInspection && customIssue !== '') {
                                        addIssue(
                                            inventoryInspection,
                                            undefined,
                                            customIssue,
                                        )
                                    }
                                }}
                                disabled={!customIssue.trim()}
                                sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    padding: theme.spacing(1.5, 3),
                                    borderRadius: 2,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                    '&:disabled': {
                                        backgroundColor: theme.palette.grey[600],
                                        color: theme.palette.grey[400],
                                    }
                                }}
                            >
                                Add Issue
                            </Button>
                        </FlexDiv>
                    </Box>
                )}

                {/* Footer */}
                <Box
                    sx={{
                        backgroundColor: '#2a2f35',
                        borderRadius: 3,
                        padding: 3,
                        border: `1px solid ${theme.palette.grey[700]}`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        textAlign: 'center',
                    }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                            navigate(`../select-area/${leaseId}/`)
                        }}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            padding: theme.spacing(1.5, 4),
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            }
                        }}
                    >
                        Finish {inventoryConfig?.name}
                    </Button>
                </Box>
            </FlexDiv>
        </ResponsivePageContainer>
    )
}
